import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getManagedDeviceByPublicId } from "../../api/Agreement";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";

const formatBytes = (bytes) => {
  if (bytes == null) return "-";
  return (bytes / 1073741824).toFixed(1) + " GB";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString();
};

const DetailRow = ({ label, value }) => (
  <div className="flex py-2 border-b border-gray-100">
    <span className="w-56 text-gray-500 font-medium">{label}</span>
    <span className="text-gray-900">{value || "-"}</span>
  </div>
);

const DeviceInventoryDetailPage = () => {
  const { managedDevicePublicId, agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (managedDevicePublicId) {
      handleGetDevice();
    }
  }, [managedDevicePublicId]);

  const handleGetDevice = async () => {
    try {
      const accessToken = await getAccessToken();
      const result = await getManagedDeviceByPublicId(managedDevicePublicId, accessToken);
      setDevice(result);
    } catch (error) {
      console.error("Failed to fetch managed device:", error);
    }
    setLoading(false);
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Device details"} loading={loading} />
            </div>

            <div className={`${loading ? "loading" : ""} page-content pb-[25rem]`}>
              <Breadcrumbs />
              <div className="flex items-center gap-4 mb-4">
                <button
                  onClick={() => navigate(`/${agreementPublicId}/device-inventory`)}
                  className="text-primary-500 hover:text-primary-700"
                >
                  <FontAwesomeIcon icon={faArrowLeftLong} className="mr-2" />
                  Back to Device inventory
                </button>
              </div>

              {device && (
                <>
                  <h2 className="text-2xl text-black font-bold pb-4">
                    {device.deviceName || "Unknown Device"}
                  </h2>

                  <div className="rounded-md shadow-md border w-full p-6">
                    <h3 className="text-lg font-semibold mb-3">Device information</h3>
                    <DetailRow label="Device Name" value={device.deviceName} />
                    <DetailRow label="Manufacturer" value={device.manufacturer} />
                    <DetailRow label="Model" value={device.model} />
                    <DetailRow label="Serial Number" value={device.serialNumber} />
                    <DetailRow label="OS" value={device.operatingSystem} />
                    <DetailRow label="OS Version" value={device.osVersion} />
                    <DetailRow label="Join Type" value={device.joinType} />
                    <DetailRow label="Enrolled" value={formatDate(device.enrolledDateTime)} />
                    <DetailRow label="Last Check-in" value={formatDate(device.lastSyncDateTime)} />
                  </div>

                  <div className="rounded-md shadow-md border w-full p-6 mt-4">
                    <h3 className="text-lg font-semibold mb-3">Primary user</h3>
                    <DetailRow label="Display Name" value={device.primaryUserDisplayName} />
                    <DetailRow label="Email" value={device.primaryUserEmail} />
                  </div>

                  <div className="rounded-md shadow-md border w-full p-6 mt-4">
                    <h3 className="text-lg font-semibold mb-3">Hardware</h3>
                    <DetailRow label="Total Storage" value={formatBytes(device.totalStorageSpaceInBytes)} />
                    <DetailRow label="Free Storage" value={formatBytes(device.freeStorageSpaceInBytes)} />
                    <DetailRow label="Physical Memory" value={formatBytes(device.physicalMemoryInBytes)} />
                    <DetailRow label="CPU Model" value={device.cpuModel} />
                    <DetailRow label="CPU Cores" value={device.cpuCoreCount} />
                    <DetailRow
                      label="CPU Clock Speed"
                      value={device.cpuMaxClockSpeedMhz ? `${device.cpuMaxClockSpeedMhz} MHz` : null}
                    />
                  </div>

                  <div className="rounded-md shadow-md border w-full p-6 mt-4">
                    <h3 className="text-lg font-semibold mb-3">Security & compliance</h3>
                    <DetailRow label="Compliance State" value={device.complianceState} />
                    <DetailRow
                      label="Encrypted"
                      value={device.isEncrypted == null ? "-" : device.isEncrypted ? "Yes" : "No"}
                    />
                  </div>

                  <div className="rounded-md shadow-md border w-full p-6 mt-4">
                    <h3 className="text-lg font-semibold mb-3">Sync info</h3>
                    <DetailRow label="Created" value={formatDate(device.createdDateTimeUTC)} />
                    <DetailRow label="Last Updated" value={formatDate(device.updatedDateTimeUTC)} />
                  </div>
                </>
              )}

              {!loading && !device && (
                <div className="text-gray-500 text-center mt-8">
                  Device not found.
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default DeviceInventoryDetailPage;
